import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { LocationService, Position, LocationPermissionStatus } from '../../services/location.service';

@Component({
  selector: 'app-location',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './location.component.html',
  styleUrl: './location.component.scss'
})
export class LocationComponent implements OnInit, OnDestroy {
  position: Position | null = null;
  permissionStatus: LocationPermissionStatus = {
    granted: false,
    denied: false,
    prompt: true
  };
  loading = false;
  watching = false;
  
  private positionSubscription?: Subscription;
  private permissionSubscription?: Subscription;
  private watchSubscription?: Subscription;

  constructor(private locationService: LocationService) {}

  ngOnInit(): void {
    this.subscribeToLocation();
    this.subscribeToPermissions();
  }

  ngOnDestroy(): void {
    this.positionSubscription?.unsubscribe();
    this.permissionSubscription?.unsubscribe();
    this.watchSubscription?.unsubscribe();
  }

  private subscribeToLocation(): void {
    this.positionSubscription = this.locationService.position$.subscribe({
      next: (position) => {
        this.position = position;
        this.loading = false;
      }
    });
  }

  private subscribeToPermissions(): void {
    this.permissionSubscription = this.locationService.permission$.subscribe({
      next: (status) => {
        this.permissionStatus = status;
        this.loading = false;
      }
    });
  }

  async requestPermission(): Promise<void> {
    this.loading = true;
    try {
      await this.locationService.requestPermission();
    } catch (error) {
      console.error('Error requesting permission:', error);
      this.loading = false;
    }
  }

  getCurrentLocation(): void {
    this.loading = true;
    this.locationService.getCurrentPosition().subscribe({
      next: (position) => {
        this.position = position;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error getting location:', error);
        this.loading = false;
      }
    });
  }

  toggleWatchLocation(): void {
    if (this.watching) {
      this.stopWatching();
    } else {
      this.startWatching();
    }
  }

  private startWatching(): void {
    this.watching = true;
    this.watchSubscription = this.locationService.watchPosition().subscribe({
      next: (position) => {
        this.position = position;
      },
      error: (error) => {
        console.error('Error watching location:', error);
        this.watching = false;
      }
    });
  }

  private stopWatching(): void {
    this.watching = false;
    this.watchSubscription?.unsubscribe();
  }

  getLocationUrl(): string {
    if (this.position) {
      return `https://maps.google.com/maps?q=${this.position.latitude},${this.position.longitude}`;
    }
    return '';
  }

  formatTimestamp(timestamp: number): string {
    return new Date(timestamp).toLocaleString();
  }

  copyCoordinates(): void {
    if (this.position && navigator.clipboard) {
      const coordinates = `${this.position.latitude}, ${this.position.longitude}`;
      navigator.clipboard.writeText(coordinates).then(() => {
        // Could add a toast notification here
        console.log('Coordinates copied to clipboard');
      }).catch(err => {
        console.error('Failed to copy coordinates:', err);
      });
    }
  }

  canShare(): boolean {
    return 'share' in navigator;
  }

  shareLocation(): void {
    if (this.position && navigator.share) {
      const url = this.getLocationUrl();
      navigator.share({
        title: 'Mi ubicación',
        text: `Mi ubicación actual: ${this.position.latitude}, ${this.position.longitude}`,
        url: url
      }).catch(err => {
        console.error('Error sharing location:', err);
      });
    }
  }
}
